import { useRef } from 'react';
import type { ChangeEvent, ClipboardEvent, KeyboardEvent } from 'react';

/**
 * OtpInput — the six-box one-time-code field.
 *
 * Corpus, every range re-read in the corpus at its current line numbers:
 * `SI:72-77` (the code label, the row and the six boxes),
 * `:73` (the row — flex, `justify-content: flex-start`, and `dir="ltr"`),
 * `:74` (`hint-placeholder-count="6"`), `:75` (every box value, the per-box
 * `aria-label` form and the green focus edge), `:326` (`codeReady` is
 * `length === 6` and `/^\d{6}$/`), `:347` (six boxes), `:353` and `:380-381`
 * (the caller's verify button, live only once the code is ready),
 * `:360-379` (`onCodeInput`/`onCodeKey` — where auto-advance, paste
 * distribution and backspace actually live), `:79-88` (the resend countdown,
 * "not you" and the demo hint, which are the caller's controls and not this
 * atom's).
 *
 * Six boxes, one value. They are a single text field drawn in six cells, so
 * the value can never carry a hole: deleting a digit closes the gap and a
 * keystroke aimed past the last digit lands on the first empty box, the same
 * way a caret cannot sit past the end of an input. The corpus keeps its six
 * DOM values independent and collapses them only when it reads them (`:373`),
 * which lets a hole show on screen that the code string does not have.
 *
 * `dir="ltr"` is the corpus's own (`:73`) and is deliberate: a numeric code is
 * read left to right in every locale. The arrows therefore keep their visual
 * meaning and are never swapped for Arabic.
 *
 * The corpus binds only `onInput` and `onKeyDown` (`:75`). A digit typed into
 * a box that already holds one is taken on `keydown` here instead, because
 * `maxLength=1` refuses the insertion that `onInput` would have reported and a
 * mistyped digit would be uncorrectable without a delete first. Everything
 * else — the empty box, autofill, paste — still arrives through the change
 * handler, which is where a soft keyboard and an IME land.
 *
 * Two consequences of taking a digit on `keydown`, both pinned in
 * `otp-input.test.tsx`. A digit held with Ctrl, Cmd or Alt belongs to the
 * browser and is left there. And a box takes focus with its digit selected, so
 * a character this field does not accept would otherwise replace that digit
 * with nothing: a value that strips to empty is refused, and only a box the
 * reader actually emptied clears.
 *
 * Divergences, disclosed rather than smoothed over. The corpus box sets
 * `outline: none`, which cancels the ring its own global rule installs
 * (`:17`) and leaves a border colour as the entire focus cue; it is not
 * carried across, so the green edge and the ring are both kept. `disabled`
 * has no corpus instance and borrows `button.css`'s opacity rather than
 * inventing a colour pair. There is no `error` prop: no design file draws a
 * rejected code — `:88` discloses that any six digits pass in the preview
 * build — and the first one belongs in a change raised against the corpus.
 *
 * Values with no token, named rather than quietly inlined: the box width
 * `clamp(34px, 10.5vw, 44px)`, height `clamp(44px, 13vw, 54px)` and font size
 * `clamp(18px, 5vw, 22px)` (`:75`) stay raw, a tracked debt —
 * flattening them to a fixed token puts six boxes over a 320px screen. The
 * row gap `clamp(5px, 1.6vw, 8px)` (`:73`) uses `--rp-space-2`, its ceiling.
 *
 * Every string a person reads arrives from the caller. This file
 * holds one space and no words: a box's name is `label` joined to whatever
 * `positionLabel` returns, so the corpus's `"… 1 / 6"` form and its separator
 * are the caller's and translatable.
 */
export interface OtpInputProps {
  /** The whole code as one string; this component owns the split across boxes. */
  readonly value: string;
  readonly onChange: (value: string) => void;
  /** Fires once every box is filled. */
  readonly onComplete?: (value: string) => void;
  /**
   * Both corpus codes are six digits (`SI:74`, `:326`), and that
   * is the default. A whole number of boxes from 1 to ten; anything else falls
   * back to the default rather than throwing — see `boxCount`.
   */
  readonly length?: number;
  /** Names the group, and leads each box's own name. */
  readonly label: string;
  /** The position phrase. `(1, 6) => '1 / 6'` reproduces `SI:75`. */
  readonly positionLabel?: (index: number, total: number) => string;
  readonly disabled?: boolean;
  readonly autoFocus?: boolean;
}

// ASCII 0-9, Arabic-Indic ٠-٩ (U+0660-0669, the digits an Arabic keyboard
// actually types), Extended Arabic-Indic ۰-۹ (U+06F0-06F9, Persian/Urdu) and
// Fullwidth ０-９ (U+FF10-FF19, what a zh-Hans IME's numeric input commits).
// `inputMode="numeric"` puts exactly these keypads in front of a reader, so a
// box that only accepted ASCII rejected the digit its own keyboard sent.
const DIGIT_CHAR = '0-9\\u0660-\\u0669\\u06F0-\\u06F9\\uFF10-\\uFF19';
const NON_DIGIT = new RegExp(`[^${DIGIT_CHAR}]`, 'g');
const DIGIT = new RegExp(`^[${DIGIT_CHAR}]$`);

/**
 * Every accepted digit, reduced to its ASCII form before it ever reaches
 * `onChange` or `onComplete`. `SI:326`'s own readiness check is
 * `/^\d{6}$/`, and a caller's verification layer downstream of this atom
 * follows the same ASCII contract — an Arabic or Persian keyboard's numeric
 * keypad is an INPUT method this field accepts, never a second alphabet the
 * emitted code is allowed to be spelled in. Display is untouched: what a box
 * shows still tracks the `value` prop exactly as supplied, non-ASCII digits
 * included, because normalising an external caller's own state on its behalf
 * is not this atom's to do.
 */
const ARABIC_INDIC_START = 0x0660;
const EXTENDED_ARABIC_INDIC_START = 0x06f0;
const FULLWIDTH_START = 0xff10;
function toAsciiDigits(text: string): string {
  return [...text]
    .map((char) => {
      const code = char.codePointAt(0) ?? 0;
      if (code >= ARABIC_INDIC_START && code <= ARABIC_INDIC_START + 9) {
        return String(code - ARABIC_INDIC_START);
      }
      if (code >= EXTENDED_ARABIC_INDIC_START && code <= EXTENDED_ARABIC_INDIC_START + 9) {
        return String(code - EXTENDED_ARABIC_INDIC_START);
      }
      if (code >= FULLWIDTH_START && code <= FULLWIDTH_START + 9) {
        return String(code - FULLWIDTH_START);
      }
      return char;
    })
    .join('');
}

/** Both corpus codes (`SI:74`, `:326`). */
const DEFAULT_LENGTH = 6;
/**
 * RFC 4226 §5.3's dynamic truncation is taken modulo `10^Digit` from a 31-bit
 * value, so an HOTP/TOTP code cannot exceed ten digits and an eleventh box is
 * not a longer code but a mistake. A ceiling is needed at all because `length`
 * is a public `number`: `Array.from({ length })` throws a `RangeError` above
 * `2^32 - 1` and on `Infinity`, and a code field that takes the sign-in page
 * down with it is a worse answer to a bad prop than a bounded one.
 */
const MAX_LENGTH = 10;

/**
 * A whole number of boxes, or the documented default. A fractional `length`
 * would render `trunc(length)` boxes while `commit` compared `next.length`
 * against the unrounded prop, so `onComplete` could never fire — a code field
 * that silently never completes, which is precisely the failure nobody reports
 * as a bug against this component.
 */
function boxCount(length: number): number {
  if (!Number.isInteger(length) || length < 1 || length > MAX_LENGTH) return DEFAULT_LENGTH;
  return length;
}

export function OtpInput({
  value,
  onChange,
  onComplete,
  length: requestedLength = DEFAULT_LENGTH,
  label,
  positionLabel,
  disabled,
  autoFocus,
}: OtpInputProps) {
  const row = useRef<HTMLDivElement>(null);
  const length = boxCount(requestedLength);
  const digits = value.replace(NON_DIGIT, '').slice(0, length);

  const boxes = (): HTMLInputElement[] => Array.from(row.current?.querySelectorAll('input') ?? []);
  const focusBox = (index: number) => {
    boxes()[Math.min(Math.max(index, 0), length - 1)]?.focus();
  };

  const cells = () => Array.from({ length }, (_, index) => digits[index] ?? '');

  const commit = (next: string) => {
    const ascii = toAsciiDigits(next);
    onChange(ascii);
    if (ascii.length === length) onComplete?.(ascii);
  };

  /** Overwrites forwards from `start`, and answers how many boxes it filled. */
  const write = (start: number, incoming: string) => {
    const next = cells();
    const run = incoming.slice(0, length - start);
    for (const [offset, digit] of [...run].entries()) next[start + offset] = digit;
    commit(next.join(''));
    return run.length;
  };

  const clear = (index: number) => {
    const next = cells();
    // Backspace steps back onto a box that may already be empty, and reporting
    // a value identical to the one the caller holds is a re-render, a cleared
    // error message or an analytics event that nothing happened to earn.
    if (next[index] === '') return;
    next[index] = '';
    onChange(toAsciiDigits(next.join('')));
  };

  const handleChange = (index: number) => (event: ChangeEvent<HTMLInputElement>) => {
    const raw = event.target.value;
    const incoming = raw.replace(NON_DIGIT, '');
    if (incoming === '') {
      // An emptied box is a deletion and clears. A box holding only characters
      // this field does not take is a rejected keystroke: the box is focused
      // with its digit selected, so a letter typed over it would otherwise
      // strip to nothing and delete a digit nobody asked to lose. Returning
      // leaves React to restore the digit from `value`.
      if (raw === '') clear(index);
      return;
    }
    const start = Math.min(index, digits.length);
    focusBox(start + write(start, incoming));
  };

  const handleKeyDown = (index: number) => (event: KeyboardEvent<HTMLInputElement>) => {
    // Ctrl/Cmd/Alt with a key is the browser's or the OS's, not this field's:
    // Cmd-2 switches tab and Alt-Left goes back. Claiming them would both
    // enter a digit nobody typed and swallow the shortcut.
    if (event.ctrlKey || event.metaKey || event.altKey) return;
    // A box already holding a digit refuses a second character at
    // `maxLength=1`, so a mistyped digit could not be corrected in place. The
    // replacement is taken here rather than left to the selection below,
    // because whether a selection makes room under `maxLength` is a thing
    // engines disagree about and a keystroke that sometimes does nothing is
    // worse than one that never did.
    if (DIGIT.test(event.key) && event.currentTarget.value !== '') {
      event.preventDefault();
      const start = Math.min(index, digits.length);
      focusBox(start + write(start, event.key));
      return;
    }
    if (event.key === 'Backspace' && event.currentTarget.value === '' && index > 0) {
      // Without this the browser applies the deletion to whichever box focus
      // has just moved to, which is a race two engines do not agree on.
      event.preventDefault();
      clear(index - 1);
      focusBox(index - 1);
      return;
    }
    if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
      event.preventDefault();
      focusBox(event.key === 'ArrowLeft' ? index - 1 : index + 1);
    }
  };

  // Taken on the group: `maxLength=1` makes the browser drop all but the first
  // character of a paste, so a per-box handler would lose five digits.
  const handlePaste = (event: ClipboardEvent<HTMLDivElement>) => {
    // Refused before it is read: whatever was on the clipboard, dropping it
    // into a box verbatim is never what a code field should do.
    event.preventDefault();
    const incoming = event.clipboardData.getData('text').replace(NON_DIGIT, '');
    if (incoming === '') return;
    const focused = boxes().findIndex((box) => box === document.activeElement);
    // A whole code pasted into a later box would land as a fragment, so a run
    // that fills the field starts at the first box however focus got there.
    const start = incoming.length >= length || focused < 0 ? 0 : Math.min(focused, digits.length);
    focusBox(start + write(start, incoming));
  };

  const firstEmpty = Math.min(digits.length, length - 1);

  return (
    <div
      ref={row}
      role="group"
      aria-label={label}
      dir="ltr"
      className="rp-otp-input"
      onPaste={handlePaste}
    >
      {Array.from({ length }, (_, index) => (
        <input
          key={index}
          className="rp-otp-input-box"
          type="text"
          inputMode="numeric"
          maxLength={1}
          // The platform's SMS fill targets one field, and it is the first.
          autoComplete={index === 0 ? 'one-time-code' : 'off'}
          autoFocus={autoFocus === true && index === firstEmpty}
          disabled={disabled}
          value={digits[index] ?? ''}
          aria-label={`${label} ${positionLabel === undefined ? index + 1 : positionLabel(index + 1, length)}`}
          // The soft-keyboard half of the overtype above: an Android key press
          // arrives as `key: 'Unidentified'` and never matches there, so the
          // digit already in the box is selected and the insertion replaces it
          // rather than being refused at `maxLength`.
          onFocus={(event) => event.currentTarget.select()}
          onChange={handleChange(index)}
          onKeyDown={handleKeyDown(index)}
        />
      ))}
    </div>
  );
}
